import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, withModifiers as _withModifiers, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "image-upload-wall" }
const _hoisted_2 = { class: "image-list" }
const _hoisted_3 = ["draggable", "onDragstart", "onDragover", "onDragenter", "onDragleave", "onDrop"]
const _hoisted_4 = ["src", "alt"]
const _hoisted_5 = { class: "image-overlay" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = ["onClick"]
const _hoisted_8 = ["onClick"]
const _hoisted_9 = ["onClick"]
const _hoisted_10 = {
  key: 0,
  class: "primary-badge"
}
const _hoisted_11 = {
  key: 1,
  class: "disabled-badge"
}
const _hoisted_12 = ["title"]
const _hoisted_13 = {
  key: 0,
  class: "px-1 py-1 text-center text-xs text-gray-500"
}
const _hoisted_14 = { key: 0 }
const _hoisted_15 = { key: 1 }
const _hoisted_16 = { key: 2 }
const _hoisted_17 = {
  key: 1,
  class: "px-1 py-1 text-center text-xs text-gray-500"
}
const _hoisted_18 = { class: "upload-btn" }
const _hoisted_19 = { class: "preview-image-wrapper" }
const _hoisted_20 = ["src"]
const _hoisted_21 = {
  key: 0,
  class: "preview-info"
}

import { ref, watch, computed, nextTick, onMounted, onUnmounted } from 'vue';
  import { message } from 'ant-design-vue';
  import {
    PlusOutlined,
    EyeOutlined,
    StarOutlined,
    StarFilled,
    DeleteOutlined,
    LeftOutlined,
    RightOutlined,
    CheckCircleOutlined,
  } from '@ant-design/icons-vue';
  import type { UploadProps } from 'ant-design-vue';
  import { uploadFile, type UploadedFileInfo } from '@/service/fileUtil';
  import {
    compressImage,
    formatImageSize,
    fetchImageSize,
    IMAGE_COMPRESSION_THRESHOLD,
    IMAGE_UPLOAD_WARNING_THRESHOLD,
  } from '@/utils/imageCompression';

  export interface ImageItem {
    uid: string;
    name: string;
    url: string;
    fileInfo?: UploadedFileInfo; // 上传后的文件信息
    isPrimary?: boolean; // 是否为主图
    sort?: number; // 排序字段
    attachmentStatus?: number; // 附件状态：0启用，1禁用
    originalSize?: number;
    compressedSize?: number;
    compressionFailed?: boolean;
  }

  interface Props {
    modelValue?: ImageItem[];
    maxCount?: number; // 最大上传数量
    compressBeforeUpload?: boolean;
  }

  interface Emits {
    (e: 'update:modelValue', value: ImageItem[]): void;
    (e: 'change', value: ImageItem[]): void;
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'ImageUploadWall',
  props: {
    modelValue: { default: () => [] },
    maxCount: { default: 10 },
    compressBeforeUpload: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "change"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

  const props = __props;

  const emit = __emit;

  const imageList = ref<ImageItem[]>([]);
  const existingImageSizes = ref(new Map<string, number | null>());
  const pendingImageSizes = new Set<string>();
  const imageSizeController = new AbortController();

  onUnmounted(() => imageSizeController.abort());

  watch(
    () =>
      props.compressBeforeUpload
        ? imageList.value
            .filter((item) => item.originalSize === undefined && item.fileInfo?.size === undefined)
            .map((item) => item.url)
        : [],
    (urls) => {
      urls.forEach(async (url) => {
        if (existingImageSizes.value.has(url) || pendingImageSizes.has(url)) return;
        pendingImageSizes.add(url);
        try {
          const size = await fetchImageSize(url, imageSizeController.signal);
          existingImageSizes.value.set(url, size);
        } catch (error) {
          if (!imageSizeController.signal.aborted) {
            console.warn('获取已有图片大小失败:', url, error);
            existingImageSizes.value.set(url, null);
          }
        } finally {
          pendingImageSizes.delete(url);
        }
      });
    },
    { immediate: true },
  );
  const previewVisible = ref(false);
  const previewImage = ref('');
  const currentPreviewIndex = ref(0);
  const dragIndex = ref<number | null>(null);
  const dragOverIndex = ref<number | null>(null);

  // 是否正在更新内部列表（防止循环更新）
  const isUpdatingInternal = ref(false);

  // 深度比较两个数组是否相同
  const isArrayEqual = (arr1: ImageItem[], arr2: ImageItem[]): boolean => {
    if (arr1.length !== arr2.length) {
      return false;
    }
    if (arr1.length === 0 && arr2.length === 0) {
      return true;
    }
    return arr1.every(
      (item, index) =>
        item.uid === arr2[index]?.uid &&
        item.url === arr2[index]?.url &&
        item.isPrimary === arr2[index]?.isPrimary &&
        item.attachmentStatus === arr2[index]?.attachmentStatus,
    );
  };

  // 监听外部传入的 modelValue
  watch(
    () => props.modelValue,
    (newVal) => {
      // 如果正在更新内部列表，跳过此次更新
      if (isUpdatingInternal.value) {
        return;
      }

      const newList = newVal || [];
      const currentList = imageList.value;

      // 如果内容相同，不需要更新
      if (isArrayEqual(currentList, newList)) {
        return;
      }

      if (newList.length > 0) {
        // 确保只有一张图片是主图
        const list = [...newList];
        let primaryFound = false;
        list.forEach((item, index) => {
          if (item.isPrimary) {
            if (primaryFound) {
              // 如果已经找到主图，将后续的主图标记取消
              item.isPrimary = false;
            } else {
              primaryFound = true;
            }
          }
        });
        // 如果没有主图，设置第一张为主图
        if (!primaryFound && list.length > 0) {
          list[0].isPrimary = true;
        }
        imageList.value = list;
      } else {
        imageList.value = [];
      }
    },
    { immediate: true, deep: true },
  );

  // 监听内部 imageList 变化，同步到外部
  watch(
    imageList,
    (newVal) => {
      // 如果正在更新内部列表，跳过此次更新
      if (isUpdatingInternal.value) {
        return;
      }

      const externalList = props.modelValue || [];
      // 如果内容相同，不需要触发更新
      if (isArrayEqual(newVal, externalList)) {
        return;
      }

      // 更新排序字段
      newVal.forEach((item, index) => {
        item.sort = index + 1;
      });

      // 标记正在更新，防止循环
      isUpdatingInternal.value = true;
      emit('update:modelValue', [...newVal]);
      emit('change', [...newVal]);
      // 使用 nextTick 确保更新完成后再重置标志
      nextTick(() => {
        isUpdatingInternal.value = false;
      });
    },
    { deep: true },
  );

  // 上传前的校验
  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    console.log('🔍 [beforeUpload] 开始校验文件:', {
      name: file.name,
      type: file.type,
      size: file.size,
      currentImageCount: imageList.value.length,
      maxCount: props.maxCount,
    });

    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      console.log('❌ [beforeUpload] 文件类型不符合要求');
      message.error('只能上传图片文件！');
      return false;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!props.compressBeforeUpload && !isLt10M) {
      console.log('❌ [beforeUpload] 文件大小超过限制');
      message.error('图片大小不能超过 10MB！');
      return false;
    }
    if (imageList.value.length >= props.maxCount) {
      console.log('❌ [beforeUpload] 图片数量已达上限');
      message.warning(`最多只能上传 ${props.maxCount} 张图片！`);
      return false;
    }
    console.log('✅ [beforeUpload] 校验通过，允许上传');
    // 返回 true 允许继续，custom-request 会被调用
    return true;
  };

  // 自定义上传
  const handleCustomRequest: UploadProps['customRequest'] = async (options) => {
    console.log('🚀 [handleCustomRequest] 开始自定义上传', options);
    const { file, onSuccess, onError, onProgress } = options;

    if (!(file instanceof File)) {
      console.error('❌ [handleCustomRequest] 文件对象为空');
      message.error('文件对象为空，无法上传');
      onError?.(new Error('文件对象为空'));
      return;
    }

    try {
      console.log('📤 [handleCustomRequest] 准备上传文件:', {
        name: file.name,
        type: file.type,
        size: file.size,
      });

      // 显示上传进度
      onProgress?.({ percent: 10 });
      console.log('📊 [handleCustomRequest] 上传进度: 10%');

      const shouldCompress = props.compressBeforeUpload && file.size > IMAGE_COMPRESSION_THRESHOLD;
      let uploadImage = file;
      let compressionFailed = false;
      if (shouldCompress) {
        try {
          uploadImage = await compressImage(file);
          if (uploadImage.size > IMAGE_UPLOAD_WARNING_THRESHOLD) {
            message.warning(
              `${file.name} 压缩后仍超过 1 MB（${formatImageSize(uploadImage.size)}），将继续上传`,
              5,
            );
          }
        } catch (error) {
          console.error('图片压缩失败，将上传原图:', error);
          compressionFailed = true;
          message.warning(`${file.name} 压缩失败，将继续上传原图`, 5);
        }
      }
      const formData = new FormData();
      formData.append('file', uploadImage);
      console.log('📦 [handleCustomRequest] FormData 已创建');

      onProgress?.({ percent: 30 });
      console.log('📊 [handleCustomRequest] 上传进度: 30%');

      console.log('🌐 [handleCustomRequest] 开始调用 uploadFile API...');
      const resp: UploadedFileInfo = await uploadFile(formData);
      console.log('✅ [handleCustomRequest] API 调用成功，响应:', resp);

      onProgress?.({ percent: 90 });
      console.log('📊 [handleCustomRequest] 上传进度: 90%');

      // 创建图片项
      const imageItem: ImageItem = {
        uid: `${Date.now()}-${Math.random()}`,
        name: resp.originalName || file.name,
        url: resp.viewUrl,
        fileInfo: resp,
        isPrimary: imageList.value.length === 0, // 第一张自动设为主图
        attachmentStatus: 0, // 默认启用
        originalSize: file.size,
        compressedSize: shouldCompress && !compressionFailed ? uploadImage.size : undefined,
        compressionFailed,
      };

      console.log('🖼️ [handleCustomRequest] 创建图片项:', imageItem);
      imageList.value.push(imageItem);
      console.log('📋 [handleCustomRequest] 当前图片列表:', imageList.value);

      onProgress?.({ percent: 100 });
      console.log('📊 [handleCustomRequest] 上传进度: 100%');
      message.success('图片上传成功');
      onSuccess?.(resp);
      console.log('🎉 [handleCustomRequest] 上传完成');
    } catch (error) {
      console.error('❌ [handleCustomRequest] 图片上传失败:', error);
      console.error('❌ [handleCustomRequest] 错误详情:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      const uploadError = error instanceof Error ? error : new Error('图片上传失败，请稍后重试');
      message.error(uploadError.message);
      onError?.(uploadError);
    }
  };

  // 设置主图
  const handleSetPrimary = (index: number) => {
    // 取消其他主图
    imageList.value.forEach((item) => {
      item.isPrimary = false;
    });
    // 设置新的主图
    imageList.value[index].isPrimary = true;
    message.success('已设置为主图');
  };

  // 切换启用/禁用状态
  const handleToggleStatus = (index: number) => {
    // 确保状态值是数字类型
    const currentStatus = Number(imageList.value[index].attachmentStatus ?? 0);
    imageList.value[index].attachmentStatus = currentStatus === 0 ? 1 : 0;
    // watch 会自动触发更新，不需要手动调用
    message.success(imageList.value[index].attachmentStatus === 0 ? '已启用' : '已禁用');
  };

  // 删除图片
  const handleRemove = (index: number) => {
    const removed = imageList.value[index];
    imageList.value.splice(index, 1);

    // 如果删除的是主图，且还有其他图片，自动设置第一张为主图
    if (removed.isPrimary && imageList.value.length > 0) {
      imageList.value[0].isPrimary = true;
    }

    message.success('已删除图片');
  };

  // 预览图片
  const handlePreview = (item: ImageItem) => {
    const index = imageList.value.findIndex((img) => img.uid === item.uid);
    currentPreviewIndex.value = index >= 0 ? index : 0;
    previewImage.value = item.url;
    previewVisible.value = true;
  };

  // 切换到上一张
  const handlePrevImage = () => {
    if (currentPreviewIndex.value > 0) {
      currentPreviewIndex.value--;
      previewImage.value = imageList.value[currentPreviewIndex.value].url;
    }
  };

  // 切换到下一张
  const handleNextImage = () => {
    if (currentPreviewIndex.value < imageList.value.length - 1) {
      currentPreviewIndex.value++;
      previewImage.value = imageList.value[currentPreviewIndex.value].url;
    }
  };

  // 键盘事件处理
  const handlePreviewKeydown = (e: KeyboardEvent) => {
    if (!previewVisible.value) return;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handlePrevImage();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      handleNextImage();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      previewVisible.value = false;
    }
  };

  // 监听预览模态框打开时，添加全局键盘事件
  watch(previewVisible, (isVisible) => {
    if (isVisible) {
      // 模态框打开时，聚焦到容器以便接收键盘事件
      nextTick(() => {
        const container = document.querySelector('.preview-container');
        if (container) {
          (container as HTMLElement).focus();
        }
      });
    }
  });

  // 上传组件 change 事件
  const handleUploadChange = (info: any) => {
    console.log('📝 [handleUploadChange] 上传组件 change 事件触发:', info);
    const { file, fileList } = info;
    console.log('📝 [handleUploadChange] 文件信息:', {
      name: file?.name,
      status: file?.status,
      percent: file?.percent,
    });
    console.log('📝 [handleUploadChange] 文件列表:', fileList);
  };

  // 拖拽排序相关方法
  const handleDragStart = (index: number, event: DragEvent) => {
    // 如果图片被禁用，不允许拖拽
    if (imageList.value[index].attachmentStatus === 1) {
      event.preventDefault();
      return;
    }

    // 如果点击的是按钮，不触发拖拽
    const target = event.target as HTMLElement;
    if (target.closest('.action-icon-btn')) {
      event.preventDefault();
      return;
    }

    dragIndex.value = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', String(index));
    }
  };

  const handleDragOver = (index: number, event: DragEvent) => {
    // 如果目标图片被禁用，不允许拖拽到此处
    if (imageList.value[index].attachmentStatus === 1) {
      return;
    }
    event.preventDefault();
    if (dragIndex.value !== null && dragIndex.value !== index) {
      dragOverIndex.value = index;
    }
  };

  const handleDragEnter = (index: number, event: DragEvent) => {
    // 如果目标图片被禁用，不允许拖拽到此处
    if (imageList.value[index].attachmentStatus === 1) {
      return;
    }
    event.preventDefault();
    if (dragIndex.value !== null && dragIndex.value !== index) {
      dragOverIndex.value = index;
    }
  };

  const handleDragLeave = (index: number, event: DragEvent) => {
    // 只有当离开的是当前悬停的元素时才清除
    if (dragOverIndex.value === index) {
      dragOverIndex.value = null;
    }
  };

  const handleDrop = (index: number, event: DragEvent) => {
    event.preventDefault();
    // 如果目标图片被禁用，不允许拖拽到此处
    if (imageList.value[index].attachmentStatus === 1) {
      dragIndex.value = null;
      dragOverIndex.value = null;
      return;
    }
    if (dragIndex.value === null || dragIndex.value === index) {
      return;
    }

    const draggedItem = imageList.value[dragIndex.value];
    // 如果被拖拽的图片被禁用，不允许拖拽
    if (draggedItem.attachmentStatus === 1) {
      dragIndex.value = null;
      dragOverIndex.value = null;
      return;
    }

    const newList = [...imageList.value];

    // 移除被拖拽的元素
    newList.splice(dragIndex.value, 1);
    // 插入到新位置
    newList.splice(index, 0, draggedItem);

    // 更新列表
    imageList.value = newList;

    // 重置拖拽状态
    dragIndex.value = null;
    dragOverIndex.value = null;
  };

  const handleDragEnd = () => {
    dragIndex.value = null;
    dragOverIndex.value = null;
  };

  // 暴露方法供父组件调用
  __expose({
    getImageList: () => imageList.value,
    getPrimaryImage: () => imageList.value.find((item) => item.isPrimary),
    clearAll: () => {
      imageList.value = [];
    },
  });

return (_ctx: any,_cache: any) => {
  const _component_a_tooltip = _resolveComponent("a-tooltip")!
  const _component_a_switch = _resolveComponent("a-switch")!
  const _component_a_upload = _resolveComponent("a-upload")!
  const _component_a_modal = _resolveComponent("a-modal")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(imageList.value, (item, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: item.uid || index,
          class: _normalizeClass(["image-item", {
          'is-primary': item.isPrimary,
          'is-disabled': item.attachmentStatus === 1,
          dragging: dragIndex.value === index,
          'drag-over': dragOverIndex.value === index,
        }]),
          draggable: item.attachmentStatus !== 1,
          onDragstart: ($event: any) => (handleDragStart(index, $event)),
          onDragover: _withModifiers(($event: any) => (handleDragOver(index, $event)), ["prevent"]),
          onDragenter: _withModifiers(($event: any) => (handleDragEnter(index, $event)), ["prevent"]),
          onDragleave: ($event: any) => (handleDragLeave(index, $event)),
          onDrop: ($event: any) => (handleDrop(index, $event)),
          onDragend: handleDragEnd
        }, [
          _createElementVNode("div", {
            class: "image-wrapper",
            onClick: _cache[10] || (_cache[10] = _withModifiers(() => {}, ["stop"]))
          }, [
            _createElementVNode("img", {
              src: item.url,
              alt: item.name
            }, null, 8, _hoisted_4),
            _createElementVNode("div", _hoisted_5, [
              (item.attachmentStatus !== 1)
                ? (_openBlock(), _createBlock(_component_a_tooltip, {
                    key: 0,
                    title: "预览"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", {
                        class: "action-icon-btn preview-icon-btn",
                        onClick: _withModifiers(($event: any) => (handlePreview(item)), ["stop"]),
                        onMousedown: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
                        onDragstart: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
                      }, [
                        _createVNode(_unref(EyeOutlined))
                      ], 40, _hoisted_6)
                    ]),
                    _: 2
                  }, 1024))
                : _createCommentVNode("", true),
              _createVNode(_component_a_tooltip, {
                title: item.attachmentStatus === 0 ? '禁用' : '启用'
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", {
                    class: "action-icon-btn status-icon-btn",
                    onClick: _withModifiers(($event: any) => (handleToggleStatus(index)), ["stop"]),
                    onMousedown: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["stop"])),
                    onDragstart: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["stop"]))
                  }, [
                    _createVNode(_component_a_switch, {
                      checked: item.attachmentStatus === 0,
                      size: "small",
                      "checked-value": 0,
                      "unchecked-value": 1,
                      onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"])),
                      onChange: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
                    }, null, 8, ["checked"])
                  ], 40, _hoisted_7)
                ]),
                _: 2
              }, 1032, ["title"]),
              (item.attachmentStatus !== 1)
                ? (_openBlock(), _createBlock(_component_a_tooltip, {
                    key: 1,
                    title: "删除"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", {
                        class: "action-icon-btn delete-icon-btn",
                        onClick: _withModifiers(($event: any) => (handleRemove(index)), ["stop"]),
                        onMousedown: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["stop"])),
                        onDragstart: _cache[7] || (_cache[7] = _withModifiers(() => {}, ["stop"]))
                      }, [
                        _createVNode(_unref(DeleteOutlined))
                      ], 40, _hoisted_8)
                    ]),
                    _: 2
                  }, 1024))
                : _createCommentVNode("", true),
              (!item.isPrimary && item.attachmentStatus !== 1)
                ? (_openBlock(), _createBlock(_component_a_tooltip, {
                    key: 2,
                    title: "设为主图"
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", {
                        class: "action-icon-btn set-primary-icon-btn",
                        onClick: _withModifiers(($event: any) => (handleSetPrimary(index)), ["stop"]),
                        onMousedown: _cache[8] || (_cache[8] = _withModifiers(() => {}, ["stop"])),
                        onDragstart: _cache[9] || (_cache[9] = _withModifiers(() => {}, ["stop"]))
                      }, [
                        _createVNode(_unref(CheckCircleOutlined))
                      ], 40, _hoisted_9)
                    ]),
                    _: 2
                  }, 1024))
                : _createCommentVNode("", true)
            ]),
            (item.isPrimary)
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, "主图"))
              : _createCommentVNode("", true),
            (item.attachmentStatus === 1)
              ? (_openBlock(), _createElementBlock("div", _hoisted_11, "已禁用"))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", {
            class: "image-name",
            title: item.name
          }, _toDisplayString(item.name), 9, _hoisted_12),
          (_ctx.compressBeforeUpload && item.originalSize !== undefined)
            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                _createElementVNode("div", null, "原始：" + _toDisplayString(_unref(formatImageSize)(item.originalSize)), 1),
                (item.compressedSize !== undefined)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, " 压缩后：" + _toDisplayString(_unref(formatImageSize)(item.compressedSize)), 1))
                  : (item.compressionFailed)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_15, "压缩失败，已上传原图"))
                    : (_openBlock(), _createElementBlock("div", _hoisted_16, "无需压缩"))
              ]))
            : (_ctx.compressBeforeUpload)
              ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                  (item.fileInfo?.size !== undefined)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createTextVNode(" 当前大小：" + _toDisplayString(_unref(formatImageSize)(item.fileInfo.size)), 1)
                      ], 64))
                    : (existingImageSizes.value.get(item.url) === null)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createTextVNode(" 当前大小：无法获取 ")
                        ], 64))
                      : (existingImageSizes.value.get(item.url) !== undefined)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                            _createTextVNode(" 当前大小：" + _toDisplayString(_unref(formatImageSize)(existingImageSizes.value.get(item.url) ?? 0)), 1)
                          ], 64))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                            _createTextVNode("当前大小：读取中")
                          ], 64))
                ]))
              : _createCommentVNode("", true)
        ], 42, _hoisted_3))
      }), 128)),
      (imageList.value.length < _ctx.maxCount)
        ? (_openBlock(), _createBlock(_component_a_upload, {
            key: 0,
            "list-type": "picture-card",
            "before-upload": beforeUpload,
            "custom-request": handleCustomRequest,
            multiple: true,
            accept: "image/*",
            "show-upload-list": false,
            class: "upload-item",
            onChange: handleUploadChange
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_18, [
                _createVNode(_unref(PlusOutlined)),
                _cache[12] || (_cache[12] = _createElementVNode("div", { class: "upload-text" }, "上传图片", -1))
              ])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_component_a_modal, {
      open: previewVisible.value,
      "onUpdate:open": _cache[11] || (_cache[11] = ($event: any) => ((previewVisible).value = $event)),
      footer: null,
      centered: "",
      width: 800,
      class: "preview-modal"
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", {
          class: "preview-container",
          tabindex: "0",
          onKeydown: handlePreviewKeydown
        }, [
          _createElementVNode("div", _hoisted_19, [
            _createElementVNode("img", {
              src: previewImage.value,
              class: "preview-image",
              alt: "预览"
            }, null, 8, _hoisted_20),
            (imageList.value.length > 1)
              ? (_openBlock(), _createElementBlock("div", _hoisted_21, _toDisplayString(currentPreviewIndex.value + 1) + " / " + _toDisplayString(imageList.value.length), 1))
              : _createCommentVNode("", true)
          ]),
          (imageList.value.length > 1)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: _normalizeClass(["preview-nav-btn prev-btn", { disabled: currentPreviewIndex.value === 0 }]),
                onClick: handlePrevImage
              }, [
                _createVNode(_unref(LeftOutlined))
              ], 2))
            : _createCommentVNode("", true),
          (imageList.value.length > 1)
            ? (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: _normalizeClass(["preview-nav-btn next-btn", { disabled: currentPreviewIndex.value === imageList.value.length - 1 }]),
                onClick: handleNextImage
              }, [
                _createVNode(_unref(RightOutlined))
              ], 2))
            : _createCommentVNode("", true)
        ], 32)
      ]),
      _: 1
    }, 8, ["open"])
  ]))
}
}

})